import { useState, type FormEvent } from "react";
import { buildStepKey } from "../../shared/staff-lifecycle";
import { docExcerpt, formatNoteTime } from "../DeliverySections";
import { translateCount } from "../i18n";
import { noteListHeadingKey } from "../staff-feedback";
import { StaffNoteText, StaffVoiceNote, staffAudioUrl } from "./FeedbackNotes";
import { confirmForClient, formatDate, makeSafeT } from "./core";
import { deliverableActionAvailability } from "./CallsPane";
import { parseValue, type StaffPaneProps } from "./console-types";
import { materialBuildNotices, materialViewSource, staffActionKindForTruth, staffStepTruth } from "./index";
import { publishIssueKey } from "./publish-issue";
import { StaffMaterialOverlay } from "./MaterialOverlay";
import { BuildProgress } from "../BuildProgress";
export function DeliverablesPane(props: StaffPaneProps) {
  const [kind, setKind] = useState("audit");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [openKind, setOpenKind] = useState<string | null>(null);
  async function create(event: FormEvent) {
    event.preventDefault();
    await props.run(`/api/admin/clients/${props.detail.client.id}/deliverables`, { method: "POST", body: { kind, title, urlOrR2: url, status: "draft" } });
  }
  // One card per MATERIAL, not per version: the daily surface is "open and
  // work"; the per-version admin editors hide behind a fold.
  const groups = new Map<string, Array<Record<string, unknown>>>();
  for (const item of props.detail.deliverables) {
    const kind = String(item.kind);
    groups.set(kind, [...(groups.get(kind) ?? []), item]);
  }
  for (const rows of groups.values()) rows.sort((a, b) => Number(b.version ?? 0) - Number(a.version ?? 0));
  // Compact two-column tiles mirroring the client's sales package (excerpt +
  // one primary action); the old single column left half the pane empty for
  // partners and stacked five equally loud buttons (night round N19).
  return <div className="material-pane"><div className="material-tile-grid">{groups.size ? [...groups.entries()].map(([kind, rows]) => {
    const current = rows[0];
    const status = String(current.status ?? "draft");
    const currentUrl = typeof current.urlOrR2 === "string" && current.urlOrR2.startsWith("http") ? current.urlOrR2 : null;
    // A deliverable whose content IS a web page must not excerpt raw
    // "<!doctype html>" as its description; the same dispatch the review
    // room uses decides what is a readable document.
    const view = materialViewSource(currentUrl, typeof current.contentMd === "string" ? current.contentMd : null);
    const excerpt = view.mode === "doc" ? docExcerpt(view.contentMd) : "";
    // Publish gate (D8): the tile says out loud when the newest version still
    // waits for the "Zveřejnit klientovi" action.
    const awaitingPublish = !(current.published === 1 || current.published === true)
      && ["review", "approved"].includes(status);
    return (
      <article className="staff-card material-card doc-tile" key={kind}>
        <div className="card-row">
          <strong>{makeSafeT(props.t)(`sales.kind.${kind}`, kind)}</strong>
          <span className="doc-tile-chips">
            {awaitingPublish && <span className="note-state-chip is-sent">{props.t("staffConsole.material.unpublishedChip")}</span>}
            <span className={`note-state-chip ${status === "approved" ? "is-handled" : "is-new"}`}>
              {props.t(`staffConsole.deliverableState.${status}`)}
            </span>
          </span>
        </div>
        {excerpt && <p className="doc-tile-excerpt">{excerpt}</p>}
        <div className="doc-tile-actions">
          <button className="primary-button compact-button" type="button" onClick={() => setOpenKind(kind)}>{props.t("staffConsole.material.open")}</button>
          {currentUrl && <a className="quiet-link" href={currentUrl} target="_blank" rel="noreferrer">{props.t("staffConsole.openDeliverable")}</a>}
          <small>
            {props.t("sales.version").replace("{version}", String(current.version))}
            {rows.length > 1 ? ` · ${translateCount(props.locale, "staffConsole.material.versionsCount", rows.length)}` : ""}
          </small>
        </div>
        {props.role === "admin" && (
          <details className="material-admin">
            <summary>{props.t("staffConsole.material.manageVersions")}</summary>
            {rows.map((item) => (
              <DeliverableCard key={String(item.id)} item={item} isLatest={String(item.id) === String(current.id)} {...props} onOpen={() => setOpenKind(kind)} />
            ))}
          </details>
        )}
      </article>
    );
  }) : <p className="staff-empty">{props.t("staffConsole.emptyDeliverables")}</p>}
  {openKind && <StaffMaterialOverlay {...props} kind={openKind} onClose={() => setOpenKind(null)} />}
  </div>{props.role === "admin" && (
    <form className="staff-card editor-form" onSubmit={create}>
      <h3>{props.t("staffConsole.deliverableEditor")}</h3>
      <select value={kind} onChange={(event) => setKind(event.target.value)}>{deliverableKinds.map((item) => <option key={item} value={item}>{makeSafeT(props.t)(`sales.kind.${item}`, item.replaceAll("_", " "))}</option>)}</select>
      <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={props.t("staffConsole.title")} required />
      <input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://" type="url" required />
      <button className="primary-button" type="submit">{props.t("staffConsole.create")}</button>
    </form>
  )}</div>;
}

export function MaterialReviewCard(props: StaffPaneProps & { kind: string; step: string }) {
  const [open, setOpen] = useState(false);
  const rows = props.detail.deliverables
    .filter((row) => String(row.kind) === props.kind)
    .sort((a, b) => Number(b.version ?? 0) - Number(a.version ?? 0));
  const current = rows[0];
  const rowIds = new Set(rows.map((row) => String(row.id)));
  const notes = props.detail.feedback.filter((row) =>
    String(row.deliverableKind ?? "") === props.kind
    || rowIds.has(String(row.deliverableId ?? "")));
  const openNotes = notes.filter((row) => String(row.status) === "new");
  // The heading names the SET; every row names its own author underneath it.
  const notesHeadingKey = noteListHeadingKey(openNotes);
  // One mapping for "does this run belong to this material" (shared module),
  // so the card, the rail and the worker's notification hold agree.
  const run = props.detail.dfyRuns.find((row) => buildStepKey(row.gateKey) === buildStepKey(props.kind));
  const published = current?.published === 1 || current?.published === true;
  const status = String(current?.status ?? "");
  // One truth: the card chip is the canonical step truth (fixr4). A locked
  // step reads locked, and a legacy output published before its step
  // unlocked is the explicit "hotové napřed" anomaly, never a plain lock
  // that simultaneously claims the client sees the material (audit a2-03).
  const truth = staffStepTruth(props.detail, props.step);
  const state = staffActionKindForTruth(truth);
  const notices = materialBuildNotices({
    runStatus: run?.status ? String(run.status) : null,
    hasDeliverable: Boolean(current)
  });
  const [retryState, setRetryState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  async function retryBuild() {
    setRetryState("sending");
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/dfy-build`,
      { method: "POST", body: { gate: props.kind } },
      { refresh: false }
    );
    setRetryState(response.ok ? "sent" : "error");
  }
  const liveVersion = rows.find((row) => row.published === 1 || row.published === true);
  // Mirror of the client's alexNoteState: an open sales_edit_request that
  // targets THIS material means the notes are already on their way.
  const editRow = props.detail.resources.find((row) => String(row.key) === "sales_edit_request");
  const edit = editRow ? parseValue(editRow) : null;
  const editState: "sent" | "working" | null = edit
    && edit.status === "new"
    && typeof edit.deliverable_id === "string"
    && rowIds.has(edit.deliverable_id)
    ? (typeof edit.served_at === "string" && edit.served_at ? "working" : "sent")
    : null;
  const publishable = Boolean(current) && !published && ["review", "approved"].includes(status);
  // The version on the card is already old news: a newer one is being built.
  // The review actions stay reachable (nothing here is dead), they just stop
  // presenting themselves as the thing to do next.
  const supersededByRun = Boolean(current) && String(run?.status ?? "") === "running";

  // A named publish refusal prints its sentence here, beside the button that
  // earned it; anything unnamed stays with the console's generic toast.
  const [publishIssue, setPublishIssue] = useState<string | null>(null);
  async function publish() {
    if (!current || !confirmForClient(props, "staffConsole.confirmDeliverablePublish")) return;
    setPublishIssue(null);
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/deliverables/${String(current.id)}/publish`,
      { method: "POST", body: {} }
    );
    setPublishIssue(await publishIssueKey(response));
  }

  return (
    <article
      className={`staff-material-review state-${state}`}
      data-material-kind={props.kind}
      data-work-state={state}
    >
      <header>
        <div>
          <p className="panel-label">{props.t("partnerOS.workspace.reviewCard")}</p>
          <h3>{makeSafeT(props.t)(`sales.kind.${props.kind}`, props.kind)}</h3>
        </div>
        <span className={`staff-work-state ${state}`}>
          {props.t(truth.aheadOfUnlock ? "partnerOS.workState.done_ahead" : `partnerOS.workState.${state}`)}
        </span>
      </header>
      {truth.aheadOfUnlock && (
        <p className="staff-material-ahead-note">
          {props.t("partnerOS.workspace.aheadOfUnlock")}
        </p>
      )}
      {/* Build status belongs to the CARD, not to the "a document exists"
          branch. Reported by Pavel 2026-08-03: a failed sales page build that
          never produced anything showed the chip "Zablokované" over the words
          "Pro tuto fázi zatím není připravený materiál", with no reason given
          and nothing to click - because the failure notice and its retry lived
          inside the branch that only renders once a document exists, which is
          exactly when they are least needed. */}
      {/* A running build over an existing version is not a status line, it is
          THE message (F54, Pavel 2026-08-05): the builder delivers after each
          revision round, so the version on the card is already superseded and
          reviewing it burns the partner's morning twice over. The step count
          stays as the detail underneath. */}
      {/* "Hotových kroků: 6" was a number with no denominator and no clock -
          six of how many, and how long is this going to take (Pavel
          2026-08-05)? The F54 sentence stays as the message; the timeline
          underneath is the detail, the same one the client and the queue read. */}
      {notices.running && run && (
        <div
          className={supersededByRun ? "staff-material-superseded" : "staff-material-progress"}
          data-build-superseded={supersededByRun ? "true" : "false"}
        >
          {supersededByRun && (
            <p className="staff-material-superseded-lead" role="status">
              <span className="loading-pulse" />
              <strong>{props.t("partnerOS.workspace.newerVersionBuilding")}</strong>
            </p>
          )}
          <BuildProgress
            gateKey={run.gateKey}
            steps={run.steps}
            stepManifest={run.stepManifest ?? null}
            startedAt={run.startedAt}
            timings={run.timings ?? null}
            eta={run.eta ?? null}
            locale={props.locale}
            t={props.t}
            density="panel"
            showReadyAt
          />
        </div>
      )}
      {notices.failure && run && (
        // A message with no way out leaves the partner asking "and what am I
        // supposed to do about it?" (Pavel 2026-08-03). Relaunching resumes
        // where the build stopped.
        <div className="message error-message staff-build-failed" role="alert">
          <p>
            {props.t("partnerOS.workspace.buildFailed")}
            {run.note ? ` ${run.note}` : ""}
          </p>
          <button
            className="secondary-button compact-button"
            disabled={retryState === "sending"}
            onClick={() => void retryBuild()}
            type="button"
          >
            {retryState === "sending"
              ? props.t("common.loading")
              : retryState === "sent"
                ? props.t("staffConsole.buildRetry.sent")
                : props.t("staffConsole.buildRetry.action")}
          </button>
          {retryState === "error" && (
            <small>{props.t("staffConsole.buildRetry.error")}</small>
          )}
        </div>
      )}
      {current ? (
        <>
          <div className="staff-material-review-meta">
            <span>{props.t("sales.version").replace("{version}", String(current.version ?? 0))}</span>
            <span>{props.t(`staffConsole.deliverableState.${status}`)}</span>
            <span>
              {published
                ? props.t("partnerOS.workspace.visibleToClient")
                : liveVersion
                  ? props.t("partnerOS.workspace.clientSeesVersion").replace("{version}", String(liveVersion.version))
                  : props.t("partnerOS.workspace.notVisibleToClient")}
            </span>
          </div>
          {/* The client already sees "sent to Alex / Alex is working" on their
              own notes; the partner saw nothing and could send the same notes
              twice (V8 audit). Same truth, both sides. */}
          {editState && (
            <p className="staff-material-progress" role="status">
              <span className="loading-pulse" />
              {props.t(editState === "working"
                ? "staffConsole.material.alexWorking"
                : "staffConsole.material.alexQueued")}
            </p>
          )}
          <div className={`staff-material-review-actions${supersededByRun ? " is-muted" : ""}`}>
            <button
              className={supersededByRun ? "secondary-button" : "primary-button"}
              data-review-room-open={props.kind}
              onClick={() => setOpen(true)}
              type="button"
            >
              {props.t("partnerOS.workspace.openReview")}
            </button>
            {publishable && (
              <button
                className={supersededByRun ? "text-button" : "secondary-button"}
                onClick={() => void publish()}
                type="button"
              >
                {props.t("staffConsole.material.publish")}
              </button>
            )}
          </div>
          {publishIssue && (
            <p className="message error-message" role="alert">{props.t(publishIssue)}</p>
          )}
          {/* The note anatomy the product already speaks: date, author, the
              text, then the action at its FOOT. This list used to invent its
              own - a bare 12 px paragraph in a `1fr 175px` grid - so the same
              dictation that reads fine in the review room came out at 95
              characters a line on a desktop, 13 on a phone, with no date and
              no author over a heading that called it the client's (audit N02,
              N03, N04). Same components, same classes as `FeedbackPane` and
              the review room, so there is one note card to maintain. */}
          <section className="staff-material-notes" aria-label={props.t(notesHeadingKey)}>
            {/* The "0" badge next to "Žádná otevřená připomínka" said the same
                thing twice; the count now appears only when there is one. */}
            <div className="staff-material-notes-head">
              <h4>{props.t(notesHeadingKey)}</h4>
              {openNotes.length > 0 && <span>{openNotes.length}</span>}
            </div>
            {openNotes.length > 0 ? (
              <ol className="doc-notes-list">
                {openNotes.map((note) => (
                  <li className="doc-note" key={String(note.id)}>
                    <div className="doc-note-head">
                      {typeof note.createdAt === "string" && (
                        <time dateTime={String(note.createdAt)}>{formatNoteTime(String(note.createdAt))}</time>
                      )}
                      {typeof note.displayName === "string" && (
                        <span className="doc-note-author">{String(note.displayName)}</span>
                      )}
                    </div>
                    {(note.text ?? note.transcript) != null
                      ? <StaffNoteText text={String(note.text ?? note.transcript)} t={props.t} />
                      : <StaffVoiceNote
                          text={null}
                          audioUrl={staffAudioUrl(props.detail.client.id, note)}
                          transcriptStatus={typeof note.transcriptStatus === "string" ? note.transcriptStatus : null}
                          t={props.t}
                        />}
                    <div className="doc-note-foot">
                      <button className="quiet-link note-resolve" type="button" onClick={() => void props.run(
                        `/api/staff/clients/${props.detail.client.id}/feedback/${String(note.id)}`,
                        { method: "PATCH", body: { status: "processed" } }
                      )}>
                        {props.t("staffConsole.process")}
                      </button>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="staff-empty">{props.t("partnerOS.workspace.noOpenFeedback")}</p>
            )}
          </section>
          {props.role === "admin" && (
            <details className="material-admin">
              <summary>{props.t("staffConsole.material.manageVersions")}</summary>
              {rows.map((row) => (
                <DeliverableCard key={String(row.id)} item={row} isLatest={String(row.id) === String(current.id)} {...props} onOpen={() => setOpen(true)} />
              ))}
            </details>
          )}
        </>
      ) : (
        notices.emptyText && (
          <div className="staff-workspace-empty">
            <p>
              {props.t(notices.emptyText === "waiting"
                ? "partnerOS.workspace.waitingForBuild"
                : "partnerOS.workspace.noMaterial")}
            </p>
          </div>
        )
      )}
      {open && <StaffMaterialOverlay {...props} kind={props.kind} onClose={() => setOpen(false)} />}
    </article>
  );
}

export function DeliverableCard(props: StaffPaneProps & {
  item: Record<string, unknown>;
  isLatest?: boolean;
  onOpen: () => void;
}) {
  const { item } = props;
  const [title, setTitle] = useState(String(item.title ?? ""));
  const [url, setUrl] = useState(String(item.urlOrR2 ?? ""));
  const [status, setStatus] = useState(String(item.status ?? "draft"));
  const published = item.published === 1 || item.published === true;
  const externalUrl = /^https?:\/\//.test(url);
  const inlineContent = url.startsWith("inline:");
  const actions = deliverableActionAvailability({ status, published }, props.role);
  const path = `/api/admin/clients/${props.detail.client.id}/deliverables/${String(item.id)}`;
  async function save(event: FormEvent) {
    event.preventDefault();
    await props.run(path, { method: "PATCH", body: { title, urlOrR2: url, status, published } });
  }
  const [publishIssue, setPublishIssue] = useState<string | null>(null);
  async function publish() {
    if (!confirmForClient(props, "staffConsole.confirmDeliverablePublish")) return;
    // Publish goes through the gate endpoint (D8) so the client notification
    // fires; a draft first moves to review via the ordinary edit.
    if (status === "draft") {
      await props.run(path, { method: "PATCH", body: { title, urlOrR2: url, status: "review", published: false } }, { refresh: false });
    }
    setPublishIssue(null);
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/deliverables/${String(item.id)}/publish`,
      { method: "POST", body: {} }
    );
    setPublishIssue(await publishIssueKey(response));
  }
  async function remove() {
    if (!confirmForClient(props, "staffConsole.confirmDeliverableDelete")) return;
    await props.run(path, { method: "DELETE" });
  }
  return <form className="staff-card editor-form deliverable-version-card" onSubmit={save}>
    <div className="card-row deliverable-version-head">
      <strong>
        {props.t("sales.version").replace("{version}", String(item.version ?? "?"))}
        {` · ${makeSafeT(props.t)(`sales.kind.${String(item.kind)}`, String(item.kind))}`}
      </strong>
      <span className="deliverable-version-chips">
        {props.isLatest && <span>{props.t("staffConsole.material.latestVersion")}</span>}
        {published && <span>{props.t("staffConsole.material.liveVersion")}</span>}
        <span>{props.t(`staffConsole.deliverableState.${status}`)}</span>
      </span>
    </div>
    {typeof item.updatedAt === "string" && (
      <time className="deliverable-version-time" dateTime={String(item.updatedAt)}>
        {props.t("staffConsole.material.updatedAt").replace("{time}", formatDate(String(item.updatedAt), props.locale))}
      </time>
    )}
    <label>
      <span>{props.t("staffConsole.title")}</span>
      <input value={title} onChange={(event) => setTitle(event.target.value)} disabled={!actions.editable} required />
    </label>
    {inlineContent ? (
      <p className="deliverable-inline-content">{props.t("staffConsole.material.inlineContent")}</p>
    ) : (
      <label>
        <span>{props.t("staffConsole.deliverableUrl")}</span>
        <input type="url" value={url} onChange={(event) => setUrl(event.target.value)} disabled={!actions.editable} required />
      </label>
    )}
    <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label={props.t("staffConsole.deliverableStatus")} disabled={!actions.editable}>
      <option value="draft">{props.t("staffConsole.deliverableState.draft")}</option>
      <option value="review">{props.t("staffConsole.deliverableState.review")}</option>
      {status === "approved" && <option value="approved">{props.t("staffConsole.deliverableState.approved")}</option>}
    </select>
    <div className="button-row">
      <button className="primary-button" type="button" onClick={props.onOpen}>{props.t("partnerOS.workspace.openReview")}</button>
      {externalUrl && <a href={url} target="_blank" rel="noreferrer">{props.t("staffConsole.material.openPublishedPage")}</a>}
    </div>
    {actions.editable && <div className="button-row">
      <button className="secondary-button" type="submit">{props.t("staffConsole.material.saveVersion")}</button>
      {actions.canPublish && <button className="primary-button" type="button" onClick={() => void publish()}>{props.t("staffConsole.publish")}</button>}
      {actions.canDelete && <button className="text-button danger" type="button" onClick={() => void remove()}>{props.t("staffConsole.delete")}</button>}
    </div>}
    {publishIssue && (
      <p className="message error-message" role="alert">{props.t(publishIssue)}</p>
    )}
  </form>;
}
// price_proposal retired 2026-07-24 (D13); legacy rows still render via the
// makeSafeT fallback, staff just cannot create new ones.
export const deliverableKinds = ["audit", "plan", "salespage", "emails", "webinar", "avatar"];
